import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';
import { Dialog } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { RELEASE_MEDIA } from '../../../Services/release.service';
import XtramMark from './XtramMark';
import Styles from './WhatsNew.module.css';

const ReleaseMediaPreview = ({ release }) => {
    if (!release.mediaUrl) return null;
    if (release.mediaType === RELEASE_MEDIA.Video) {
        return (
            <video
                key={release.id}
                className={Styles.detailsMedia}
                src={release.mediaUrl}
                controls
                autoPlay
                muted
                loop
                playsInline
            />
        );
    }
    return <img key={release.id} className={Styles.detailsMedia} src={release.mediaUrl} alt={release.title} />;
};

function ReleaseDetailsDialog({ releases, index, onIndexChange, onClose, direction = 'rtl' }) {
    const { t } = useTranslation();
    const release = index != null ? releases[index] : null;
    const isRtl = direction === 'rtl';

    const safeBody = useMemo(() => DOMPurify.sanitize(release?.body || ''), [release?.body]);

    const hasPrev = index != null && index > 0;
    const hasNext = index != null && index < releases.length - 1;
    // "Previous" points toward the reading start, so the chevrons flip in RTL.
    const PrevIcon = isRtl ? ChevronRightRoundedIcon : ChevronLeftRoundedIcon;
    const NextIcon = isRtl ? ChevronLeftRoundedIcon : ChevronRightRoundedIcon;

    return (
        <Dialog
            open={!!release}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: Styles.detailsPaper }}
        >
            {release && (
                <div className={Styles.details} dir={direction}>
                    <div className={Styles.detailsHeader}>
                        <span className={Styles.panelHeaderMark}>
                            <XtramMark size={22} mono="#ffffff" />
                        </span>
                        <div className={Styles.panelHeaderText}>
                            <span className={Styles.detailsKicker}>{t('WhatsNewTitle')}</span>
                            <span className={Styles.detailsTitle}>{release.title}</span>
                        </div>
                        <button type="button" className={Styles.panelClose} onClick={onClose} aria-label={t('Close')}>
                            <CloseRoundedIcon sx={{ fontSize: 20 }} />
                        </button>
                    </div>

                    <div className={Styles.detailsBody}>
                        <ReleaseMediaPreview release={release} />
                        <span className={Styles.detailsDate}>
                            {moment.utc(release.publishedAtUtc).local().format('LL')}
                        </span>
                        {release.summary && <p className={Styles.detailsSummary}>{release.summary}</p>}
                        <div className={Styles.detailsContent} dangerouslySetInnerHTML={{ __html: safeBody }} />
                    </div>

                    {releases.length > 1 && (
                        <div className={Styles.detailsFooter}>
                            <button
                                type="button"
                                className={Styles.navBtn}
                                onClick={() => onIndexChange(index - 1)}
                                disabled={!hasPrev}
                            >
                                <PrevIcon sx={{ fontSize: 18 }} />
                                {t('Previous')}
                            </button>
                            <span className={Styles.navCounter}>{index + 1} / {releases.length}</span>
                            <button
                                type="button"
                                className={Styles.navBtn}
                                onClick={() => onIndexChange(index + 1)}
                                disabled={!hasNext}
                            >
                                {t('Next')}
                                <NextIcon sx={{ fontSize: 18 }} />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
}

export default ReleaseDetailsDialog;
